import React, { useState } from 'react';
import { useFormatWeight } from '../utils/units';

interface Exercise {
  exerciseName: string;
  prescribedSets: number;
  prescribedReps: string | number;
  prescribedWeight?: number;
  actualSets: number;
  actualReps: number[];
  actualWeight: number[];
  restTimes?: number[];
  videoUrl?: string;
  completed: boolean;
  instructions?: string[];
  formCues?: string[];
  images?: string[];  // 🆕 JAN 29, 2026: Exercise images for thumbnails
}

interface ExpandableExercisesProps {
  exercises: Exercise[];
}

export const ExpandableExercises: React.FC<ExpandableExercisesProps> = ({ exercises }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedExercise, setExpandedExercise] = useState<number | null>(null);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);  // 🆕 JAN 29, 2026: Modal for full-size image
  const formatWeight = useFormatWeight();

  const toggleExercise = (index: number) => {
    setExpandedExercise(expandedExercise === index ? null : index);
  };

  const openFullscreenImage = (imageUrl: string, e: React.MouseEvent) => {
    e.stopPropagation();  // Prevent exercise toggle when clicking image
    setFullscreenImage(imageUrl);
  };

  const closeFullscreenImage = () => {
    setFullscreenImage(null);
  };

  const completionRate = exercises.length > 0 ? (exercises.filter(e => e.completed).length / exercises.length) * 100 : 0;

  return (
    <>
    <details 
      className="w-full max-w-2xl mx-auto mb-4"
      open={isExpanded}
      onToggle={(e) => setIsExpanded((e.target as HTMLDetailsElement).open)}
    >
      <summary className="cursor-pointer text-center text-sm font-medium text-blue-400 hover:text-blue-300 py-2 px-4 bg-gray-800 rounded-lg transition-colors">
        Exercises ({exercises.filter(e => e.completed).length}/{exercises.length} Completed) ▼
      </summary>
      
      <div className="mt-3 bg-gray-800 rounded-lg p-4">
        {/* Summary */}
        <div className="mb-4 pb-4 border-b border-gray-700">
          <div className="text-center">
            <div className="text-sm text-gray-400 mb-1">Completion Rate</div>
            <div className="text-2xl font-bold text-white mb-2">
              {Math.round(completionRate)}%
            </div>
            <div className="text-xs text-gray-500">
              {exercises.filter(e => e.completed).length} / {exercises.length} exercises completed
            </div>
          </div>
        </div>
        
        {/* Exercise List */}
        <div className="space-y-3">
          {exercises.map((exercise, idx) => (
            <div 
              key={idx}
              className={`border rounded-lg overflow-hidden transition-all ${
                exercise.completed ? 'border-green-600' : 'border-gray-700'
              }`}
            >
              {/* Exercise Header - Always Visible */}
              <div 
                className="p-4 cursor-pointer hover:bg-gray-700/50 transition-colors"
                onClick={() => toggleExercise(idx)}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                      exercise.completed 
                        ? 'border-green-500 bg-green-500' 
                        : 'border-gray-600'
                    }`}>
                      {exercise.completed && (
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-white truncate">{exercise.exerciseName}</div>
                      <div className="text-sm text-gray-400">
                        {exercise.prescribedSets > 0 && exercise.actualSets !== exercise.prescribedSets ? (
                          <>{exercise.actualSets}/{exercise.prescribedSets} sets</>
                        ) : (
                          <>{exercise.actualSets} sets</>
                        )}
                        {' '}× {exercise.prescribedReps} reps
                        {exercise.actualWeight[0] > 0 && ` @ ${formatWeight(exercise.actualWeight[0])}`}
                        {exercise.prescribedWeight && exercise.prescribedWeight > 0 && exercise.actualWeight[0] > 0 && exercise.actualWeight[0] !== exercise.prescribedWeight && (
                          <span className={exercise.actualWeight[0] > exercise.prescribedWeight ? 'text-green-400 ml-1' : 'text-red-400 ml-1'}>
                            ({exercise.actualWeight[0] > exercise.prescribedWeight ? '+' : ''}{formatWeight(Math.round(exercise.actualWeight[0] - exercise.prescribedWeight))})
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* 🆕 JAN 29, 2026: Exercise Thumbnail (right side) */}
                  <div className="flex items-center gap-2">
                    {exercise.images && exercise.images.length > 0 && (
                      <div 
                        className="w-16 h-16 rounded-lg overflow-hidden bg-gray-900 border border-gray-700 flex-shrink-0 hover:border-blue-500 transition-colors"
                        onClick={(e) => openFullscreenImage(exercise.images![0], e)}
                      >
                        <img 
                          src={exercise.images[0]} 
                          alt={exercise.exerciseName}
                          className="w-full h-full object-cover cursor-pointer"
                        />
                      </div>
                    )}
                    <svg 
                      className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ${
                        expandedExercise === idx ? 'transform rotate-180' : ''
                      }`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Exercise Details - Expandable */}
              {expandedExercise === idx && (
                <div className="px-4 pb-4 space-y-3">
                  {/* Set by Set Breakdown: Prescribed vs Actual */}
                  <div className="bg-gray-900 rounded-lg p-3">
                    <div className="text-xs text-gray-400 font-medium mb-2">Set Details</div>
                    {exercise.prescribedWeight && exercise.prescribedWeight > 0 && (
                      <div className="text-xs text-gray-500 mb-2">
                        Rx: {exercise.prescribedSets}×{exercise.prescribedReps} @ {formatWeight(exercise.prescribedWeight)}
                      </div>
                    )}
                    <div className="space-y-2">
                      {Array.from({ length: Math.max(exercise.actualSets, exercise.prescribedSets || 0) }, (_, setIdx) => {
                        const prescribedReps = typeof exercise.prescribedReps === 'number'
                          ? exercise.prescribedReps
                          : parseInt(String(exercise.prescribedReps)) || 0;
                        const actualReps = exercise.actualReps[setIdx] || 0;
                        const actualWeight = exercise.actualWeight[setIdx] || 0;
                        const prescribedWeight = exercise.prescribedWeight || 0;
                        const weightDelta = prescribedWeight > 0 ? actualWeight - prescribedWeight : 0;
                        const repsDelta = prescribedReps > 0 ? actualReps - prescribedReps : 0;

                        return (
                          <div key={setIdx} className="flex items-center gap-3 text-sm">
                            <span className="text-gray-500 w-12 flex-shrink-0">Set {setIdx + 1}:</span>
                            <div className="flex items-center gap-2 flex-1">
                              <span className="text-white font-mono">
                                {actualWeight > 0 ? formatWeight(actualWeight) : '--'}
                              </span>
                              <span className="text-gray-400">×</span>
                              <span className="text-white font-mono">
                                {actualReps > 0 ? actualReps : prescribedReps} reps
                              </span>
                              {weightDelta !== 0 && (
                                <span className={`text-xs ${weightDelta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {weightDelta > 0 ? '+' : ''}{formatWeight(weightDelta)}
                                </span>
                              )}
                              {repsDelta !== 0 && weightDelta === 0 && (
                                <span className={`text-xs ${repsDelta > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {repsDelta > 0 ? '+' : ''}{repsDelta} reps
                                </span>
                              )}
                            </div>
                            {exercise.restTimes && exercise.restTimes[setIdx] && (
                              <span className="text-xs text-gray-500">
                                ({exercise.restTimes[setIdx]}s rest)
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Form Cues */}
                  {exercise.formCues && exercise.formCues.length > 0 && (
                    <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-3">
                      <div className="text-xs text-blue-400 font-medium mb-2">Form Cues</div>
                      <ul className="space-y-1">
                        {exercise.formCues.map((cue, cueIdx) => (
                          <li key={cueIdx} className="text-sm text-gray-300 flex items-start gap-2">
                            <span className="text-blue-400">•</span>
                            <span>{cue}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Instructions */}
                  {exercise.instructions && exercise.instructions.length > 0 && (
                    <div className="bg-gray-900 rounded-lg p-3">
                      <div className="text-xs text-gray-400 font-medium mb-2">Instructions</div>
                      <ul className="space-y-1">
                        {exercise.instructions.map((instruction, instIdx) => (
                          <li key={instIdx} className="text-sm text-gray-300 flex items-start gap-2">
                            <span className="text-gray-500">{instIdx + 1}.</span>
                            <span>{instruction}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Video Link */}
                  {exercise.videoUrl && (
                    <a 
                      href={exercise.videoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-center py-2 bg-purple-600 hover:bg-purple-700 rounded text-sm font-medium text-white transition-colors"
                    >
                      📹 View Demo Video
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </details>
    
    {/* 🆕 JAN 29, 2026: Fullscreen Image Modal */}
    {fullscreenImage && (
      <div 
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
        onClick={closeFullscreenImage}
      >
        <div className="relative max-w-4xl max-h-full">
          {/* Close Button */}
          <button
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
            onClick={closeFullscreenImage}
          >
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Full-Size Image */}
          <img 
            src={fullscreenImage} 
            alt="Exercise demonstration"
            className="w-full h-auto rounded-lg"
            onClick={(e) => e.stopPropagation()}  // Prevent closing when clicking image
          />
        </div>
      </div>
    )}
  </>
  );
};

